import { createContext, useContext, useReducer } from 'react';

import { noop } from 'lodash';

import type { MarketingTemplatePreset } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';

// identical to the MarketingTemplatePreset type
export type TemplateBuilderState = {
  deletedAtUtc: string | null;
  htmlBody: string;
  htmlBodyS3FileName: string;
  isDeleted: boolean;
  jsonBody: string;
  jsonBodyS3FileName: string;
  lastModifiedDateUtc: string | null;
  lspId: number;
  marketingTemplatePresetId: number | null;
  selectedTemplate?: MarketingTemplatePreset | null;
  templateName: string;
};

// Action types
type UpdateFullTemplateAction = {
  payload: MarketingTemplatePreset;
  type: 'update-full-template';
};

export type SetValueAction<K extends keyof TemplateBuilderState> = {
  payload: { key: K; value: TemplateBuilderState[K] };
  type: 'set-value';
};

type ResetAction = {
  type: 'reset';
};

export type Action = ResetAction | SetValueAction<keyof TemplateBuilderState> | UpdateFullTemplateAction;

// Default state
const defaultBuilderState: TemplateBuilderState = {
  deletedAtUtc: null,
  htmlBody: '',
  htmlBodyS3FileName: '',
  isDeleted: false,
  jsonBody: '',
  jsonBodyS3FileName: '',
  lspId: 0,
  marketingTemplatePresetId: 0,
  templateName: '',
  lastModifiedDateUtc: null,
};

// Reducer
export function templateBuilderReducer(state: TemplateBuilderState, action: Action): TemplateBuilderState {
  switch (action.type) {
    case 'set-value':
      return {
        ...state,
        [action.payload.key]: action.payload.value,
      };

    case 'reset':
      return defaultBuilderState;

    case 'update-full-template':
      return { ...state, ...getUpdatedStateFromCampaignTemplate(action.payload) };

    default:
      return state;
  }
}

export function getUpdatedStateFromCampaignTemplate(template: MarketingTemplatePreset): Partial<TemplateBuilderState> {
  return {
    deletedAtUtc: template.DeletedAtUtc,
    htmlBody: template.HtmlBody,
    htmlBodyS3FileName: template.HtmlBodyS3FileName,
    isDeleted: template.IsDeleted,
    jsonBody: template.JsonBody,
    jsonBodyS3FileName: template.JsonBodyS3FileName,
    lspId: template.LspId,
    marketingTemplatePresetId: template.MarketingTemplatePresetId,
    templateName: template.TemplateName,
    lastModifiedDateUtc: template.LastModifiedDateUtc,
  };
}

// Context and hooks
export function useTemplateBuilderReducer() {
  return useReducer(templateBuilderReducer, defaultBuilderState);
}

export const TemplateBuilderContext = createContext<ReturnType<typeof useTemplateBuilderReducer>>([
  defaultBuilderState,
  noop,
]);

export function useTemplateBuilderContext() {
  return useContext(TemplateBuilderContext);
}
